import { DateTime } from 'luxon';
import {
  clampAnchorToEventListBounds,
  getEventListBounds,
} from '@/features/events/lib/eventListWindow.lib';
import type { DateTimeRange } from '@/features/events/lib/range.lib';
import { getDisplayTimezone } from '@/features/experiences/lib/experience';
import type { PageExperience } from '@/features/experiences/types';
import { resolveWeekStartDay } from '@/features/layouts/weekStart.lib';
import { startOfWeekFor } from '@/utils/dates';
import type { PrintableTemplate } from '../../types';

export const getRangeBounds = (
  experience: PageExperience
): { start: DateTime; end: DateTime } =>
  getEventListBounds(experience, getDisplayTimezone(experience));

const clampAnchor = (anchor: DateTime, experience: PageExperience) =>
  clampAnchorToEventListBounds(
    anchor,
    experience,
    getDisplayTimezone(experience)
  );

export const getRangeForAnchor = (
  anchor: DateTime,
  experience?: PageExperience
): DateTimeRange => {
  const clamped = experience ? clampAnchor(anchor, experience) : anchor;
  // With an experience, resolve its per-view/per-layout week start; without one
  // (add-import bound), fall back to Sunday.
  const weekStart = startOfWeekFor(
    clamped,
    experience ? resolveWeekStartDay(experience) : 0
  );
  return {
    startDate: weekStart,
    endDate: weekStart.plus({ days: 6 }).endOf('day'),
  };
};

export const getDefaultRange: NonNullable<
  PrintableTemplate<unknown>['getDefaultRange']
> = (experience) => {
  const anchor = clampAnchor(
    DateTime.local({ zone: getDisplayTimezone(experience) }),
    experience
  );
  // Pass experience so the default range resolves the configured week-start day,
  // consistent with getRangeForAnchor and the planner grid.
  return getRangeForAnchor(anchor, experience);
};
